<template>
  <section
    ref="target"
    class="text-center"
  >
    <div class="flex items-center text-gray-900">
      <h2 class="flex-auto text-base font-semibold text-gray-900">
        {{ monthName }}
      </h2>
    </div>

    <div class="max-w-lg w-full md:max-w-none mx-auto mt-4 grid grid-cols-7 text-xs/6 text-gray-500">
      <div
        v-for="day in weekdays"
        :key="day"
      >
        {{ day }}
      </div>
    </div>

    <div class="max-w-lg w-full md:max-w-none mx-auto isolate mt-2 grid grid-cols-7 gap-y-1">
      <DatePickerDay
        v-for="({ date, isCurrentMonth, isDisabled, isSelected, isSelectedRange, isUnavailable, isSelectedRangeStart, isSelectedRangeEnd }) in monthDays"
        :key="date.toISOString()"
        :date="date"
        :is-current-month="isCurrentMonth"
        :is-selected="isSelected"
        :is-selected-range="isSelectedRange"
        :is-selected-range-start="isSelectedRangeStart"
        :is-selected-range-end="isSelectedRangeEnd"
        :is-disabled="isDisabled"
        :is-unavailable="isUnavailable"
        @date-click="emit('date-click', date)"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import type { Dayjs } from 'dayjs'
import type { BlockedRange, DaysOfWeek } from '~/types'

const props = withDefaults(defineProps<{
  month: number
  year: number
  selectedDates: Dayjs[]
  timezone?: string
  trimRow?: boolean
  monthNameFormat?: string
  ignoreLeapYear?: boolean
  minDate?: Dayjs
  maxDate?: Dayjs
  disabledDates?: BlockedRange[]
  unavailableDays?: DaysOfWeek[]
}>(), {
  monthNameFormat: 'MMMM YYYY',
})

const emit = defineEmits<{
  'date-click': [date: Dayjs]
}>()

const startingMonth = computed(() => datetime().tz(props.timezone).year(props.year).month(props.month).startOf('month'))

const monthName = computed(() => {
  return startingMonth.value.format(props.monthNameFormat)
})

const weekdays = computed(() => datetime.weekdaysMin(true))
const firstDayOfWeek = computed(() => datetime.localeData().firstDayOfWeek())

// Pre-process disabledDates into optimized lookup structures
// This computed only recalculates when disabledDates or timezone changes
const processedDisabledDates = computed(() => {
  if (!props.disabledDates || props.disabledDates.length === 0) {
    return {
      singleDates: new Set<string>(),
      ranges: [],
    }
  }

  const singleDates = new Set<string>()
  const ranges: Array<{
    start?: Dayjs
    end?: Dayjs
    hasStart: boolean
    hasEnd: boolean
  }> = []

  for (const d of props.disabledDates) {
    if (typeof d === 'object' && d !== null && 'start' in d && 'end' in d) {
      // Range object - pre-process timezone conversions once
      const range: {
        start?: Dayjs
        end?: Dayjs
        hasStart: boolean
        hasEnd: boolean
      } = {
        hasStart: false,
        hasEnd: false,
      }

      if (d.start) {
        range.start = datetime.tz(d.start, props.timezone).startOf('day')
        range.hasStart = true
      }
      if (d.end) {
        range.end = datetime.tz(d.end, props.timezone).endOf('day')
        range.hasEnd = true
      }

      // Only push ranges that have at least one valid date
      if (range.hasStart || range.hasEnd) {
        ranges.push(range)
      }
    }
    else {
      // Single date - convert to string for O(1) lookup
      // Format in the same timezone context for consistent comparison
      // The date variable in monthDays is already in props.timezone, so format d the same way
      const dateStr = props.timezone ? datetime.tz(d, props.timezone).format('YYYY-MM-DD') : datetime(d).format('YYYY-MM-DD')
      singleDates.add(dateStr)
    }
  }

  return { singleDates, ranges }
})

// Helper function to check if a date is disabled using the optimized structure
function isDateDisabled(date: Dayjs, minDate?: Dayjs, maxDate?: Dayjs): boolean {
  // Check min/max date bounds first (cheap check)
  if (minDate || maxDate) {
    const min = minDate?.startOf('day')
    const max = maxDate?.endOf('day')
    if (!date.isBetween(min ?? date, max ?? date, 'day', '[]')) {
      return true
    }
  }

  const { singleDates, ranges } = processedDisabledDates.value

  // Check single dates (O(1) lookup)
  const dateStr = date.format('YYYY-MM-DD')
  if (singleDates.has(dateStr)) {
    return true
  }

  // Check ranges (O(n) but n is typically much smaller, and we can optimize further)
  for (const range of ranges) {
    if (range.hasStart && range.hasEnd && range.start && range.end) {
      if (date.isBetween(range.start, range.end, 'day', '[]')) {
        return true
      }
    }
    else if (range.hasStart && range.start) {
      if (date.isSameOrAfter(range.start, 'day')) {
        return true
      }
    }
    else if (range.hasEnd && range.end) {
      if (date.isSameOrBefore(range.end, 'day')) {
        return true
      }
    }
  }

  return false
}

const monthDays = computed(() => {
  // Get last day of month
  const lastDay = startingMonth.value.endOf('month').startOf('day')

  // Get days from previous month to fill first week
  const daysFromPrevMonth = (startingMonth.value.day() - firstDayOfWeek.value + 7) % 7
  const prevMonth = startingMonth.value.subtract(1, 'month').startOf('day')
  const prevMonthDays = []

  for (let i = daysFromPrevMonth - 1; i >= 0; i--) {
    prevMonthDays.push({
      date: prevMonth.endOf('month').subtract(i, 'day').startOf('day'),
      isCurrentMonth: false,
      isSelected: false,
      isSelectedRange: false,
      isSelectedRangeStart: false,
      isSelectedRangeEnd: false,
      isUnavailable: false,
      isDisabled: false,
    })
  }

  // Pre-compute min/max date boundaries once
  const minDateBoundary = props.minDate?.startOf('day')
  const maxDateBoundary = props.maxDate?.endOf('day')

  // Pre-compute selected range boundaries once
  const selectedStart = props.selectedDates[0]?.startOf('day')
  const selectedEnd = props.selectedDates[1]?.endOf('day') ?? props.selectedDates[0]?.endOf('day')

  // Get days from current month
  const currentMonthDays = []
  for (let i = 0; i <= lastDay.date() - 1; i++) {
    const date = startingMonth.value.add(i, 'days').startOf('day')

    // Ignore February 29th in leap years
    if (props.ignoreLeapYear && date.isSame(datetime().month(1).date(29), 'day')) {
      continue
    }

    const isSelectedRangeStart = selectedStart?.isSame(date, 'day') ?? false
    const isSelectedRangeEnd = props.selectedDates[1]?.startOf('day')?.isSame(date, 'day') ?? false

    currentMonthDays.push({
      date,
      isCurrentMonth: true,
      isSelected: isSelectedRangeStart || isSelectedRangeEnd,
      isSelectedRangeStart,
      isSelectedRangeEnd,
      isSelectedRange: props.selectedDates.length > 1 && selectedStart && selectedEnd ? date.isBetween(selectedStart, selectedEnd, null, '[]') : false,
      isUnavailable: props.unavailableDays?.includes((date.day() + 1) as DaysOfWeek),
      isDisabled: isDateDisabled(date, minDateBoundary, maxDateBoundary),
    })
  }

  // Get days from next month to fill last week
  const nextMonth = startingMonth.value.add(1, 'month').startOf('day')

  const totalDays = prevMonthDays.length + currentMonthDays.length

  const maxDays = props.trimRow && totalDays <= 35 ? 35 : 42

  const daysFromNextMonth = maxDays - totalDays
  const nextMonthDays = []

  for (let i = 1; i <= daysFromNextMonth; i++) {
    nextMonthDays.push({
      date: nextMonth.add(i, 'days').startOf('day'),
      isCurrentMonth: false,
      isSelected: false,
      isSelectedRange: false,
      isSelectedRangeStart: false,
      isSelectedRangeEnd: false,
      isUnavailable: false,
      isDisabled: false,
    })
  }

  return [...prevMonthDays, ...currentMonthDays, ...nextMonthDays]
})
</script>
